import { Link } from 'react-router-dom';
import { Button, Notice, RuledPage } from '@kollez/design-system';
import {
  Briefcase, Users, GraduationCap, BadgeCheck, Trophy, Handshake,
  Sparkles, CheckCircle2, ShieldCheck, MapPin,
} from 'lucide-react';

/**
 * /register/partner — value-forward "waiting for self-serve" page.
 *
 * Self-serve partner sign-up is still being finalised. Rather than a bare
 * "book a call" placeholder, this page uses the visit to (a) tell the
 * partner exactly which of the six partner types they fit, (b) preview
 * what Nexie does for them once they're onboarded, and (c) offer both
 * "book a call" and "accept invite" paths.
 *
 * Full self-serve 3-step wizard is designed in
 * docs/design/auth/register-partner.md and will replace this page once
 * verification/webhook infrastructure lands.
 */

const PARTNER_TYPES: Array<{ icon: React.ReactNode; name: string; hint: string }> = [
  { icon: <Briefcase size={18} />,      name: 'Companies',            hint: 'Full-time roles + internships for graduating batches' },
  { icon: <Users size={18} />,          name: 'Training providers',   hint: 'Free or paid cohorts, live or self-paced' },
  { icon: <BadgeCheck size={18} />,     name: 'Certification bodies', hint: 'Issued badges students carry across roles' },
  { icon: <Handshake size={18} />,      name: 'Mentors',              hint: '1:1 or cohort, career or skill-focused' },
  { icon: <Trophy size={18} />,         name: 'Hackathon hosts',      hint: 'Weekend, week-long, campus or online' },
  { icon: <GraduationCap size={18} />,  name: 'Programs & bootcamps', hint: 'Structured tracks with completion outcomes' },
];

const NEXIE_FOR_PARTNERS: string[] = [
  'Drafts postings from a job description — you approve before publish',
  'Ranks the shortlist with cited reasons — never a black box',
  'Flags fake applications ambiently — human review before action',
];

export function RegisterPartnerPage() {
  return (
    <RuledPage>
      <section className="max-w-page-narrow mx-auto px-6 py-16 grid gap-8">
        <div className="grid gap-2">
          <p className="eyebrow flex items-center gap-1.5">
            <Sparkles size={12} />
            For partners
          </p>
          <h1 className="font-serif text-4xl">Hire from every partner campus on Kollez.</h1>
          <p className="subhead max-w-prose">
            Companies, training providers, certification bodies, mentors and hackathon
            hosts — post any offering and reach verified students across every
            partner campus. <Link to="/ai" className="link">Nexie</Link> ranks the
            shortlist with reasons; you approve every message.
          </p>
        </div>

        <Notice tone="info">
          Self-serve partner sign-up is being finalised. Meanwhile, book a short call
          and we&apos;ll set you up the same day.
        </Notice>

        <div className="flex flex-wrap gap-3">
          <Link to="/contact?topic=partner">
            <Button variant="primary">Book a 30-min demo</Button>
          </Link>
          <Link to="/pricing?aud=partners">
            <Button variant="secondary">See pricing</Button>
          </Link>
        </div>

        <div className="grid gap-3">
          <p className="eyebrow m-0 text-xs">Six partner types</p>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {PARTNER_TYPES.map((t) => (
              <div
                key={t.name}
                className="card card-pad flex items-start gap-3"
              >
                <span
                  className="flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{
                    width: 36, height: 36,
                    background: 'rgb(var(--bg-info))',
                    color: 'rgb(var(--ink-accent))',
                  }}
                >
                  {t.icon}
                </span>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="helper m-0 text-xs mt-0.5">{t.hint}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-3 card card-pad" style={{ background: 'rgb(var(--bg-secondary))' }}>
          <p className="eyebrow m-0 text-xs flex items-center gap-1.5">
            <Sparkles size={12} />
            What Nexie does for partners
          </p>
          <ul className="grid gap-2 text-sm text-ink-secondary">
            {NEXIE_FOR_PARTNERS.map((b) => (
              <li key={b} className="flex gap-2">
                <CheckCircle2 size={14} className="mt-0.5 text-ink-success flex-shrink-0" />
                {b}
              </li>
            ))}
          </ul>
          <Link to="/ai" className="link text-sm">See how Nexie works →</Link>
        </div>

        <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-ink-tertiary" aria-label="Trust signals">
          <li className="flex items-center gap-1"><ShieldCheck size={12} className="text-ink-success" />Verified employer program</li>
          <li className="flex items-center gap-1"><Sparkles size={12} className="text-ink-accent" />Nexie AI · cites every source</li>
          <li className="flex items-center gap-1"><MapPin size={12} className="text-ink-success" />Data stays in India</li>
        </ul>

        <p className="helper">
          Already invited by a campus?{' '}
          <Link className="link" to="/accept-invite">
            Accept your invitation
          </Link>
          .
        </p>
      </section>
    </RuledPage>
  );
}
